import { JustificationReasonSave } from '@schooly/api';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { JustificationChangeModal, JustificationChangeModalData } from './JustificationChangeModal';

export const CONTEXT_NAME = 'JustificationChangeModalContext';

type JustificationChangeModalState = {
  callback: (value: JustificationReasonSave | undefined) => void;
} & JustificationChangeModalData;

type JustificationChangeModalContextProps = {
  openModal: (params: JustificationChangeModalState) => void;
};

export const JustificationChangeModalContext = createContext<JustificationChangeModalContextProps>({
  openModal: () => {},
});

export const WithJustificationChangeModal: FC<PropsWithChildren> = ({ children }) => {
  const [modalState, setModalState] = useState<JustificationChangeModalState | undefined>();

  const onSubmit = (data: JustificationReasonSave) => {
    modalState?.callback(data);
    setModalState(undefined);
  };

  const onClose = () => {
    modalState?.callback(undefined);
    setModalState(undefined);
  };

  return (
    <JustificationChangeModalContext.Provider value={{ openModal: setModalState }}>
      {children}
      {modalState && (
        <JustificationChangeModal onSubmit={onSubmit} onClose={onClose} {...modalState} />
      )}
    </JustificationChangeModalContext.Provider>
  );
};

export const useJustificationChangeModal = () => {
  const { openModal } = useContext(JustificationChangeModalContext);

  const confirmJustificationChange = useCallback(
    (state: JustificationChangeModalData): Promise<JustificationReasonSave | undefined> =>
      new Promise((res) => {
        openModal({ callback: res, ...state });
      }),
    [openModal],
  );

  return { confirmJustificationChange };
};
